import { PersistentStore, Store } from './main';
import { IThought, IThoughtFilter, IFeedbackResponseStats } from '@/models/thought.model';
import { IPager } from '@/models/pager.model';
import { RepositoryFactory } from '@/api/repositoryFactory';
import ThoughtList from '@/components/discuss/topic/ThoughtList.vue';
const ThoughtRepository = RepositoryFactory.get('thought');

interface ThoughtStoreModel extends Object {
  thoughtid: number | null;
  thoughts: IThought[];
  singleThought: IThought | null;
  thoughtFilter: {} | IThoughtFilter;
  pager: IPager;
  loading: boolean;
  maxThoughts: number;
}

const validThoughtFilters: string[] = ['topicid', 'projectid', 'module', 'voted', 'ownerid', 'title', 'text', 'sort'];

class ThoughtStore extends PersistentStore<ThoughtStoreModel> {
  protected data(): ThoughtStoreModel {
    return {
      thoughtid: null,
      thoughts: [],
      singleThought: null,
      thoughtFilter: {},
      pager: {
        rows: 15,
        page: 1,
      },
      loading: false,
      maxThoughts: 0,
    };
  }

  // thoughts
  setThoughts(thoughts: IThought[]) {
    this.state.thoughts = thoughts;
  }

  setRows(rows: number) {
    this.state.pager.rows = rows == 0 ? 15 : rows;
  }

  private resetThoughts() {
    this.state.thoughts = [];
  }
  private setThought(thought: IThought) {
    // save single-thought separately
    // to prevent overriding thought-array when multiple components are visible and not losing hidden thought
    this.state.singleThought = thought;
    this.addToThoughts(thought);
  }
  addToThoughts(thought: IThought) {
    let found = false;
    // exchange in local topics

    this.state.thoughts = this.state.thoughts.map(t => {
      if (t.id === thought.id) {
        found = true;
        return thought;
      } else {
        return t;
      }
    });
    if (!found) {
      this.state.thoughts.push(thought);
    }
  }
  private appendThoughts(thoughts: IThought[]) {
    thoughts.forEach(t => this.addToThoughts(t));
    // or ? maybe when paging?
    // Array.prototype.push.apply(this.state.thoughts, thoughts);
  }
  visibleThoughts() {
    return this.state.thoughts.filter(t => {
      if (t.status == 'show') {
        return true;
      }
    });
  }
  getThought(thoughtid: number) {
    // method to get a single thought, specifically used in Wrapper.vue
    // example: when viewing a hidden thought in detail-view
    if (this.state.singleThought && this.state.singleThought.id == thoughtid) {
      return this.state.singleThought;
    }
    return this.state.thoughts.find(t => t.id == thoughtid);
  }
  replaceThought(thought: IThought) {
    if (this.state.thoughts.length == 0) return;
    for (let i = 0; i < this.state.thoughts.length; i++) {
      if (this.state.thoughts[i].id == thought.id) {
        this.state.thoughts[i] = thought;
        return;
      }
    }
  }
  setThoughtId(thoughtid: number) {
    this.state.thoughtid = thoughtid;
    this.fetchThought(thoughtid);
  }
  fetchMoreThoughts() {
    if (this.state.maxThoughts > this.state.thoughts.length) {
      this.state.pager.page = this.state.pager.page + 1;
      this.fetchPagedThoughts(null);
    }
  }
  reloadThoughts(onFinishedCb: (() => void) | null) {
    this.state.pager.page = 1;
    this.resetThoughts();
    this.fetchPagedThoughts(onFinishedCb);
  }

  fetchThought(thoughtid: number, force = false) {
    return new Promise((resolve, reject) => {
      this.state.loading = true;

      let thought;
      // check if we already fetched the thought
      if (!force) {
        if (this.state.thoughts.length > 0) {
          thought = this.state.thoughts.find(t => t.id === thoughtid);
          if (thought) {
            this.state.loading = false;
            resolve(thought);
          }
        }
      }
      if (!thought) {
        // if not, fetch thought
        ThoughtRepository.getItem(thoughtid, 'widget_list')
          .then((thought: IThought) => {
            if (thought) {
              this.setThought(thought);
              resolve(thought);
            } else {
              reject(null);
            }
            this.state.loading = false;
          })
          .catch((error: any) => {
            reject(null);
            this.state.loading = false;
          });
      }
    });
  }

  // onFinishedCb() is called when the API call is successful.
  private fetchPagedThoughts(onFinishedCb: (() => void) | null) {
    this.state.loading = true;
    // console.log("fetching paged thoughts");
    const params = Object.assign({ permissions: 'basic' }, this.state.thoughtFilter, this.state.pager);
    ThoughtRepository.get(params)
      .then((result: any) => {
        if (result.data) {
          if (result.pager) {
            this.state.maxThoughts = result.pager.total_entries;
          }
          this.appendThoughts(result.data);
          this.state.loading = false;
        } else {
          console.log('error');
          this.state.loading = false;
        }
        if (onFinishedCb) {
          onFinishedCb();
        }
      })
      .catch((error: any) => {
        console.log(error);
        this.state.loading = false;
      });
  }

  // filter
  isValidThoughtFilter(key: string) {
    if (key === undefined || key === null) {
      return false;
    }
    if (validThoughtFilters.includes(key)) {
      return true;
    }
    if (key.match(/^attribute(\d+)$/)) {
      return true;
    }
    return false;
  }
  setThoughtFilter(params: IThoughtFilter) {
    // reset filter values completely
    this.state.thoughtFilter = {};
    // update provided key
    this.updateThoughtFilter(params);
    // refetch thoughts with updated filter
    this.reloadThoughts(null);
  }
  updateThoughtFilter(params: IThoughtFilter) {
    // update only provided keys, leave rest of filter
    for (const [key, value] of Object.entries(params)) {
      if (this.isValidThoughtFilter(key) && value) {
        Object.assign(this.state.thoughtFilter, { [key]: value });
      }
    }
  }
  updateFeedbackStatisticForThought(thoughtid: number) {
    return new Promise((resolve, reject) => {
      // fetch response stats
      ThoughtRepository.getFeedbackStatistics(thoughtid)
        .then((feedbackStats: IFeedbackResponseStats) => {
          if (feedbackStats) {
            let foundThought;
            this.state.thoughts = this.state.thoughts.map(t => {
              if (t.id === thoughtid) {
                t.feedback_response_stats = feedbackStats;
                foundThought = t;
              }
              return t;
            });
            resolve(foundThought);
          } else {
            reject(null);
          }
        })
        .catch((error: any) => {
          reject(null);
        });
    });
  }
}
export const thoughtStore = new ThoughtStore('THOUGHT_STORE');
